/**
 * Steps component module.
 * @module shared/components/Steps
 *
 */

import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import {Button, DescriptiveText, Row} from '../../shared';
import styled, {css} from 'styled-components/macro';
import {fill} from './cssUtils';

const _Wrapper = styled.div`
  height: ${props => props.height};
  ${fill()};
  margin: 5px;
`;

const _StepTitle = styled.span`
  font-family: ${props => props.theme.bodyText.name};
  font-size: 14px;
  color: ${props => props.theme.dark ? 'rgba(255,255,255, 0.95)' : 'rgba(0,0,0,0.95)'};
  text-align: left;
  ${fill()};
  overflow-wrap: break-word;
`;

const _StepCount = styled.div`
  font-family: ${props => props.theme.bodyText.name};
  font-size: 12px;
  color: ${props => props.theme.dark ? 'rgba(255,255,255, 0.7)' : 'rgba(0,0,0,0.7)'};
  text-align: left;
  width: fit-content;
`;

const _IndicatorSet = styled.div`
  ${fill()}
  margin: 5px 0 5px 0;
  display: flex;
`;

const _ErrorMessage = styled.div`
  height: auto;
  ${fill()};
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 2px;
`;

const getIndicatorColor = props => {
  if (props.index < props.currentIndex) {
    return props.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
  }
  if (props.index > props.currentIndex) {
    return props.theme.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  }
  return props.theme.primaryColor;
};

const _Indicator = styled.div`
  height: 7px;
  margin: 0 1px 0 1px;
  flex: 1;
  ${props => {
    if (props.theme.neumorphic) {
      if (props.index < props.currentIndex) {
        return css`
          box-shadow: -1px -1px 0 0 rgba(255, 255, 255, 0.55), 1px 1px 0 0 rgba(0, 0, 0, 0.3);
          background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.45));
          background-color: ${props.theme.tileBackgroundColor};
        `;
      }
      if (props.index > props.currentIndex) {
        return css`
          box-shadow: -1px -1px 0 0 rgba(255, 255, 255, 0.3), 1px 1px 0 0 rgba(0, 0, 0, 0.3);
          background-image: linear-gradient(to bottom right, rgba(255, 525, 255, 0.15), rgba(255, 255, 255, 0.15));
          background-color: ${props.theme.tileBackgroundColor};
        `;
      }
      return css`
        box-shadow: -1px -1px 0 0 rgba(255, 255, 255, 0.45), 1px 1px 0 0 rgba(0, 0, 0, 0.3);
        background-color: ${props.theme.primaryColor};
      `;
    }
    return css`
      background-color: ${getIndicatorColor(props)};
    `;
  }};
`;

const _ContentArea = styled.div`
  ${fill()}
  background-color: ${props => {
    if (props.displayContentAreaBackground) {
      return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
    }
    return '';
  }};
  border-radius: 4px;
  padding: 5px;
`;

// TODO Steps Transition spinner or something
/**
 * Steps component.
 * @name Steps
 */
const Steps = ({
  height,
  steps,
  stepIndex,
  initialStateContent,
  completedStateContent,
  canStepBack,
  cancelAction,
  errorMessage,
  setErrorMessage,
  hideStepIndicators,
  processing,
  displayContentAreaBackground,
}) => {
  useEffect(() => {
    if (errorMessage && setErrorMessage) {
      setErrorMessage(null);
    }
  }, [stepIndex, setErrorMessage]);

  let state;
  let step;
  if (stepIndex === 'INIT') {
    state = typeof initialStateContent === 'function' ? initialStateContent() : initialStateContent;
  } else if (stepIndex === 'END') {
    state = typeof completedStateContent === 'function' ? completedStateContent() : completedStateContent;
  } else {
    const currentStep = steps[stepIndex];
    if (currentStep) {
      // const showPrevious = !!(canStepBack && (stepIndex === 0 || currentStep.previous));
      step = <>
        <_StepTitle>{currentStep.title}</_StepTitle>
        {!hideStepIndicators && <>
          <_StepCount>Step {stepIndex + 1} of {steps.length}:</_StepCount>
          <_IndicatorSet>
            {steps.map((s, idx) => <_Indicator key={`indicator-${idx}`} index={idx} currentIndex={stepIndex}/>)}
          </_IndicatorSet>
        </>}
        <Row wrap={false} justifySubRow={'space between'}>
          <Button importance={'secondary'}
                  size={'md'}
                  width={'auto'}
                  disabled={false}
                  label={stepIndex > 0 && canStepBack ? 'Back' : 'Cancel'}
                  action={stepIndex > 0 && canStepBack ? currentStep.previous.action : cancelAction}/>
          <Button importance={'primary'}
                  size={'md'}
                  width={'auto'}
                  disabled={currentStep.next.disabled}
                  label={stepIndex === steps.length - 1 ? currentStep.next.label : 'Next'}
                  action={currentStep.next.action}
                  processing={processing}
                  form={currentStep.form}/>
        </Row>
        {errorMessage && <_ErrorMessage><DescriptiveText alignment={'left'}>{errorMessage}</DescriptiveText></_ErrorMessage>}
        <_ContentArea displayContentAreaBackground={displayContentAreaBackground}>{typeof currentStep.content === 'function' ? currentStep.content() : currentStep.content}</_ContentArea>
      </>;
    }
  }

  return <_Wrapper height={height}>{state || step}</_Wrapper>;
};

export default Steps;

Steps.propTypes = {
  /**
   * Height of the overall component, as CSS (e.g. '500px', '50%').
   */
  height: PropTypes.string.isRequired,
  /**
   * The array of steps
   */
  steps: (props, propName, componentName) => {
    PropTypes.checkPropTypes({
      [propName]: PropTypes.arrayOf(
        PropTypes.shape({
          /**
           * The step title
           */
          title: PropTypes.string.isRequired,
          /**
           * The step content JSX (or a function that returns it)
           */
          content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
          /**
           * Defines behavior of the the "previous" button for the step, if there is expected to be one
           */
          previous: PropTypes.shape({
            /**
             * The action to perform when the button is pressed (and not disabled)
             */
            action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
          }),
          /**
           * Defines behavior of the the "next" button for the step
           */
          next: PropTypes.shape({
            /**
             * Button label text. Only applicable to the final step
             */
            label: PropTypes.String,
            /**
             * The action to perform when the button is pressed (and not disabled)
             */
            action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
            /**
             * Whether the button is disabled
             */
            disabled: PropTypes.bool,
          }).isRequired,
          /**
           * The ID of the form to map to the step
           */
          form: PropTypes.String, // ETODO move to next?
        })
      ),
    }, props, propName, componentName);
    if (!props[propName] && !props.initialStateContent) {
      return new Error(`Must provide ${propName} to a ${componentName} component if there is no Initial State Content`);
    }
    return null;
  },
  /**
   * The numeric index of the current step to display, or a string key representing the state to display.
   */
  stepIndex: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(['INIT', 'END']),
  ]).isRequired,
  /**
   * JSX to display for an "Initial" state before all steps. Corresponds to step index "INIT".
   */
  initialStateContent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * JSX to display for a "Completed" state after all steps. Corresponds to step index "END".
   */
  completedStateContent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Determines whether previous steps can be navigated to by restricting whether a "previous" button will render.
   */
  canStepBack: PropTypes.bool,
  /**
   * The action for the Cancel button to perform.
   */
  cancelAction: PropTypes.func.isRequired,
  /**
   * The error message to display.
   */
  errorMessage: PropTypes.string,
  /**
   * If passed in error message is automatically cleared when stepIndex is changed.
   */
  setErrorMessage: PropTypes.func,
  /**
   * If specified as true, hides step count and indicators.
   */
  hideStepIndicators: PropTypes.bool,
  /**
   * Pass through to Next button processing
   */
  processing: PropTypes.bool,
  /**
   * Display Content Area Background
   */
  displayContentAreaBackground: PropTypes.bool,
};

Steps.defaultProps = {
  displayContentAreaBackground: true,
};

